/**
 * ProductCard — display de sugerencia farmacológica.
 *
 * 2026-05-08 — Cart eliminado. Sin botón "Agregar al pedido", sin badge
 * "EN EL PEDIDO", sin stepper de cantidad. La cantidad sugerida (X tabletas)
 * se muestra como referencia clínica del tratamiento. La farmacéutica usa el
 * SKU para buscar en su POS y dispensar la cantidad real (no se persiste
 * acá — esto es consulta, no venta).
 *
 * Drimian P6: NO recomputa dosis (consume card.dosis directo). DaysStepper
 * recalcula el total a entregar (card.entrega × tomas/día × días), pero dose_value queda
 * invariante.
 *
 * GUARD-04: card.disclaimer renderizado literal (NO regenerar — backend
 * hardcoded postprocess.py).
 *
 * UI-03: DoseSourceIndicator tri-state visible en posology box.
 */
import { Icon } from './icons'
import { textoPa } from '../utils/textoPa'
import { useState, type ReactNode } from 'react'
import type { ComplementarioItem, ProductCard as ProductCardSchema } from '../types/product_card'
import { copyToClipboard } from '../hooks/useClipboard'
import { DaysStepper } from './DaysStepper'
import { DoseSourceIndicator } from './DoseSourceIndicator'

interface Props {
  card: ProductCardSchema
  mode?: 'full' | 'compact'
  /** Plan 04-03 — trace_id del turno (de event:complete). Null si Langfuse off,
   *  en cuyo caso FeedbackWidget se oculta. */
  trace_id?: string | null
  /** 2026-10-01 — id de la consulta; con él el feedback funciona sin Langfuse. */
  consulta_id?: string | null
  /** Plan 04-03 — device_id de la tablet (useDeviceCreds). Requerido para que
   *  FeedbackWidget pueda hacer POST /feedback. Si ausente, no rendea widget. */
  device_id?: string
  /** 21-oct ("la decisión primero"): las acciones sobre el producto, dentro de la tarjeta. */
  acciones?: ReactNode
  /** 22-oct (Elvio): el complemento sale solo, dentro de la tarjeta; "Sumar" lo agrega al caso. */
  onSumar?: (texto: string) => void
}


export function ProductCard({
  card,
  mode = 'full',
  acciones,
  onSumar,
}: Props) {
  // Init days desde card.dosis.days (backend computa). Override solo display.
  const [days, setDays] = useState<number>(card.dosis?.days ?? 3)
  const [copied, setCopied] = useState(false)

  const presentation = [card.concentracion, card.forma]
    .filter((s) => s && s !== 'unknown')
    .join(' · ')

  const isCompact = mode === 'compact'

  const handleCopySku = async () => {
    const text = `SKU ${card.sku_id} ${card.nombre_comercial}`
    const ok = await copyToClipboard(text)
    if (ok) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="product-card">
      <div className="grid gap-1.5">
        {/* Línea 1: nombre comercial y precio en la sucursal (21-oct: la decisión primero) */}
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div
            className="text-[17px] font-bold leading-tight"
            style={{ color: 'var(--color-text)', minWidth: 0 }}
          >
            {card.nombre_comercial}
          </div>
          {!!card.precio_sucursal && card.precio_sucursal > 0 && (
            <div className="text-[17px] font-bold tabular-nums" style={{ color: 'var(--color-text)' }}>
              {bs(card.precio_sucursal)}
            </div>
          )}
        </div>

        {/* Línea 2: principio activo + presentación */}
        <div
          className="text-[12px] leading-snug"
          style={{
            color: 'var(--color-text-muted)',
          }}
        >
          <span style={{ fontWeight: 500 }}>{textoPa(card.principio_activo)}</span>
          {presentation && (
            <>
              <span style={{ margin: '0 6px' }}>·</span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{presentation}</span>
            </>
          )}
        </div>

        {/* Línea 3: SKU badge + lab + flags Hipermaxi (el plus diferencial) */}
        <div className="flex items-center gap-2 flex-wrap text-[13px]">
          <button
            type="button"
            onClick={handleCopySku}
            aria-label={`Copiar SKU ${card.sku_id}`}
            title="Click para copiar SKU al portapapeles"
            className="inline-flex items-center gap-1 transition-colors"
            style={{
              padding: '3px 8px',
              borderRadius: 999,
              background: copied ? 'var(--color-ok-soft, #d1fae5)' : 'var(--color-brand-soft)',
              color: copied ? 'var(--color-ok, #047857)' : 'var(--color-brand-ink)',
              border: '1px solid',
              borderColor: copied ? 'var(--color-ok, #10b981)' : 'var(--color-brand)',
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {copied ? 'Copiado' : `SKU ${card.sku_id}`}
          </button>
          {card.laboratorio && (
            <span style={{ color: 'var(--color-text-muted)' }}>
              Lab {card.laboratorio}
            </span>
          )}
          {card.stock_sucursal !== undefined && card.stock_sucursal !== null && (
            <StockSucursalBadge
              stock={card.stock_sucursal}
              sucursal={card.sucursal}
              precio={card.precio_sucursal && card.precio_sucursal > 0 ? null : card.precio_sucursal}
            />
          )}
          {card.stock_actualizado && horaStock(card.stock_actualizado) && (
            <span style={{ color: 'var(--color-text-muted)' }} title="Última actualización del stock">
              stock al {horaStock(card.stock_actualizado)}
            </span>
          )}
          {(card.stock_sucursal === undefined || card.stock_sucursal === null) &&
            card.stock_total !== undefined && card.stock_total !== null && card.stock_total <= 0 && (
            <span
              style={{
                padding: '2px 7px',
                borderRadius: 999,
                background: 'var(--color-warn-soft, #fee2e2)',
                color: 'var(--color-warn, #b91c1c)',
                border: '1px solid var(--color-warn, #dc2626)',
                fontWeight: 600,
              }}
            >
              <Icon.Alert width={13} height={13} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 3 }} />stock agotado
            </span>
          )}
          {card.requiere_receta === true && (
            <span style={{ color: 'var(--color-text-muted)' }}>· con receta</span>
          )}
        </div>
      </div>

      {card.aviso_producto && (
        <div
          role="status"
          className="text-[13px]"
          style={{
            padding: '6px 10px',
            borderRadius: 'var(--radius-s)',
            background: 'var(--color-surface-2)',
            color: 'var(--color-text)',
            borderLeft: '3px solid var(--color-info)',
          }}
        >
          {card.aviso_producto}
        </div>
      )}

      {card.aviso_dosis && (
        <div
          role="alert"
          className="text-[13px] font-medium"
          style={{
            padding: '8px 12px',
            borderRadius: 'var(--radius-s)',
            background: 'var(--color-warn-soft, #fee2e2)',
            color: 'var(--color-warn, #b91c1c)',
            border: '1px solid var(--color-warn, #dc2626)',
          }}
        >
          {card.aviso_dosis}
        </div>
      )}

      {card.stock_sucursal !== undefined && card.stock_sucursal !== null && card.stock_sucursal <= 0 && (
        <SinStockBlock card={card} />
      )}

      {card.dosis && card.dosis_modo && (
        <div className="pc-posology" aria-label="Cómo se toma">
          <div className="text-[15px] font-semibold leading-snug" style={{ color: 'var(--color-brand-ink)' }}>
            {card.dosis_modo.texto}
          </div>
          <div>
            <DoseSourceIndicator
              source={card.dose_source}
              reviewStatus={card.dosis.review_status}
              fuente={card.dose_fuente}
              url={card.dose_source_url}
              nota={card.dose_nota}
            />
          </div>
          {card.dosis_modo.entregar && (
            <div className="grid gap-1 mt-1">
              <span className="text-[12px] font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Entregar al cliente
              </span>
              <span className="text-[14px] leading-snug">{card.dosis_modo.entregar}</span>
            </div>
          )}
        </div>
      )}

      {card.dosis && !card.dosis_modo && (
        <div className="pc-posology">
          {/* 2026-10-07: una línea: cuánto, cada cuánto y por cuántos días */}
          <span className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
            Dosis
          </span>
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span className="text-[17px] font-bold tabular-nums">
              {card.dosis_por_peso?.ml
                ? card.dosis_por_peso.ml[0] === card.dosis_por_peso.ml[1]
                  ? `${card.dosis_por_peso.ml[0]} ml`
                  : `${card.dosis_por_peso.ml[0]}–${card.dosis_por_peso.ml[1]} ml`
                : (card.dosis_unidades ?? `${card.dosis.dose_value} ${card.dosis.unit}`).replace(/\s*\(.*\)$/, '')}
              {' '}cada {card.dosis.frequency_h} h
            </span>
            <DaysStepper value={days} onChange={setDays} />
          </div>

          {card.dosis_por_peso?.texto && (
            <div
              className="text-[14px] font-medium leading-snug"
              style={{ color: 'var(--color-brand-ink)' }}
              aria-label="Dosis por peso"
            >
              Por peso: {card.dosis_por_peso.texto}
            </div>
          )}

          {!isCompact && <PillCounter card={card} days={days} />}
          {isCompact && <CompactTotal card={card} days={days} />}

          <DoseSourceIndicator
            source={card.dose_source}
            reviewStatus={card.dosis.review_status}
            fuente={card.dose_fuente}
            url={card.dose_source_url}
            nota={card.dose_nota}
          />
        </div>
      )}

      {card.interpretation_flag && (
        <div
          className="text-[12px] italic"
          style={{ color: 'var(--color-info)' }}
        >
          ℹ {card.interpretation_flag}
        </div>
      )}

      {card.error_summary && (
        <div
          className="text-[13px]"
          style={{
            padding: '8px 12px',
            borderRadius: 'var(--radius-s)',
            background: 'var(--color-warn-soft)',
            color: 'var(--color-warn)',
            border: '1px solid var(--color-warn)',
          }}
        >
          <Icon.Alert width={13} height={13} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 3 }} />{card.error_summary}
        </div>
      )}

      {(card.complementarios ?? []).slice(0, 2).map((c) => (
        <ComplementoLinea key={c.sku_id} c={c} onSumar={onSumar} />
      ))}

      {acciones}

      {/* GUARD-04: card.disclaimer literal, NO regenerar */}
      <div
        className="text-[13px] italic mt-1"
        style={{ color: 'var(--color-text-soft)' }}
      >
        {card.disclaimer}
      </div>

      {/* 20-oct: la pregunta de feedback va una sola vez, al pie de la respuesta (ChatStream) */}
    </div>
  )
}

const nombreCorto = (s?: string | null) => (s ?? '').replace(/^FARMACIA\s+/i, '')
/** "RESPIRA SPRAY NASAL X 30 ML" → "RESPIRA SPRAY NASAL" (igual que nombre_corto del motor). */
const nombreProducto = (s: string) => s.split(/\s+X\s*\d/i)[0].trim() || s

/** 22-oct — el complemento a la vista: qué es, para qué, precio y stock acá, y "Sumar". */
function ComplementoLinea({ c, onSumar }: { c: ComplementarioItem; onSumar?: (texto: string) => void }) {
  const nombre = nombreProducto(c.nombre_comercial)
  const hay = c.stock_sucursal === undefined || c.stock_sucursal === null ? null : c.stock_sucursal > 0
  // "Lavado nasal salino: arrastra el polen y el polvo (NHS)" → "Lavado nasal salino" (lo demás, al pasar el dedo)
  const razon = (c.razon ?? '').split(/[:(]/)[0].trim()
  return (
    <div className="pc-comp" aria-label="Para sumar" title={c.razon || undefined}>
      <div className="pc-comp-texto">
        <span className="pc-comp-etiqueta">Sumale</span>{' '}
        <span className="pc-comp-nombre">{nombre}</span>
        <span className="pc-comp-dato">
          {razon ? ` · ${razon}` : ''}
          {c.precio_sucursal ? ` · ${bs(c.precio_sucursal)}` : ''}
        </span>
        {hay !== null && (
          <span className={hay ? 'pc-comp-stock' : 'pc-comp-stock sin'}>
            {' · '}{hay ? `${c.stock_sucursal} u.` : 'sin stock acá'}
          </span>
        )}
        {c.requiere_receta === true && <span className="pc-comp-dato"> · con receta</span>}
      </div>
      {onSumar && (
        <button type="button" className="accion pc-comp-sumar" onClick={() => onSumar(`Sumar ${nombre}`)}
          aria-label={`Sumar ${nombre}`}>
          Sumar
        </button>
      )}
    </div>
  )
}
const bs = (n?: number | null) =>
  n === undefined || n === null ? '' : `Bs ${n.toFixed(2).replace('.', ',')}`

/** 2026-10-10 — "que el stock esté siempre en línea" (Hipermaxi): la tarjeta dice de cuándo es. */
export function horaStock(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const hoy = new Date().toDateString() === d.toDateString()
  const hora = d.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })
  return hoy ? hora : `${d.toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit' })} ${hora}`
}

function StockSucursalBadge({
  stock,
  sucursal,
  precio,
}: {
  stock: number
  sucursal?: string | null
  precio?: number | null
}) {
  const hay = stock > 0
  return (
    <span
      style={{
        padding: '2px 7px',
        borderRadius: 999,
        background: hay ? 'var(--color-ok-soft, #d1fae5)' : 'var(--color-warn-soft, #fee2e2)',
        color: hay ? 'var(--color-ok, #047857)' : 'var(--color-warn, #b91c1c)',
        border: `1px solid ${hay ? 'var(--color-ok, #10b981)' : 'var(--color-warn, #dc2626)'}`,
        fontWeight: 600,
      }}
      title={sucursal ?? undefined}
    >
      {hay
        ? `Lo tenemos: ${stock} u. en ${nombreCorto(sucursal) || 'esta sucursal'}`
        : `No lo tenemos en ${nombreCorto(sucursal) || 'esta sucursal'}`}
      {precio ? ` · ${bs(precio)}` : ''}
    </span>
  )
}

/** Sin stock en la sucursal: equivalentes del mismo código único que sí hay + otras sucursales. */
function SinStockBlock({ card }: { card: ProductCardSchema }) {
  const eq = card.equivalentes_disponibles ?? []
  const otras = card.otras_sucursales ?? []
  if (eq.length === 0 && otras.length === 0) return null
  return (
    <div
      className="grid gap-1.5 text-[13px]"
      style={{
        padding: '8px 12px',
        borderRadius: 'var(--radius-s)',
        background: 'var(--color-brand-soft)',
        border: '1px solid var(--color-brand)',
      }}
    >
      {eq.length > 0 && (
        <>
          <div className="font-semibold" style={{ color: 'var(--color-brand-ink)' }}>
            Hay acá con el mismo principio activo, concentración y forma:
          </div>
          <ul className="grid gap-1">
            {eq.map((e) => (
              <li key={e.sku_id} className="flex flex-wrap items-baseline gap-2">
                <span style={{ fontWeight: 500 }}>{e.nombre_comercial}</span>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-muted)' }}>
                  SKU {e.sku_id}
                </span>
                {e.laboratorio && (
                  <span style={{ color: 'var(--color-text-muted)' }}>Lab {e.laboratorio}</span>
                )}
                <span style={{ color: 'var(--color-ok, #047857)', fontWeight: 600 }}>
                  {e.stock_sucursal} u.{e.precio_sucursal ? ` · ${bs(e.precio_sucursal)}` : ''}
                </span>
              </li>
            ))}
          </ul>
          {card.aviso_equivalentes && (
            <div className="text-[12px] italic" style={{ color: 'var(--color-text-muted)' }}>
              {card.aviso_equivalentes}
            </div>
          )}
        </>
      )}
      {otras.length > 0 && (
        <div className="text-[12px]" style={{ color: 'var(--color-text-muted)' }}>
          También en: {otras.map((o) => `${nombreCorto(o.sucursal)} (${o.stock})`).join(', ')}
        </div>
      )}
    </div>
  )
}

interface PillCounterProps {
  card: ProductCardSchema
  days: number
}

/** 2026-10-01 — cuánto entregar. La toma la calcula el backend con la dosis ya validada
 *  (card.entrega); acá solo se multiplica por tomas/día y días. Sin `entrega` no se muestra
 *  número (antes el front parseaba dosis y concentración por su cuenta: omeprazol 20 mg ×
 *  14 días salía "280 unidades"). */
function totalEntrega(card: ProductCardSchema, days: number): { texto: string; detalle: string } | null {
  const e = card.entrega
  if (!e || !e.por_toma || !e.tomas_dia || days <= 0) return null
  const tomas = Math.ceil(e.tomas_dia * days)
  // 2026-10-07: el precio del API es por unidad de venta (comprimido, cápsula, sobre) y por
  // frasco en los líquidos; sin precio (> 0) no se muestra total
  const precio = card.precio_sucursal && card.precio_sucursal > 0 ? card.precio_sucursal : null
  if (e.unidad === 'ml') {
    const ml = Math.round(e.por_toma * tomas)
    if (e.frasco_ml) {
      const f = Math.max(1, Math.ceil(ml / e.frasco_ml))
      return {
        texto: `${f} ${f === 1 ? 'frasco' : 'frascos'}${precio ? ` · ${bs(f * precio)}` : ''}`,
        detalle: `${ml} ml en ${days} ${days === 1 ? 'día' : 'días'} · frasco de ${e.frasco_ml} ml`,
      }
    }
    return { texto: `${ml} ml`, detalle: `en ${days} ${days === 1 ? 'día' : 'días'}` }
  }
  const n = Math.ceil(e.por_toma * tomas)
  const palabra = n === 1 ? e.unidad : `${e.unidad}s`
  const porToma = e.por_toma === 0.5 ? '½' : String(e.por_toma)
  return {
    texto: `${n} ${palabra}${precio ? ` · ${bs(n * precio)}` : ''}`,
    detalle: `${porToma} por toma × ${tomas} ${tomas === 1 ? 'toma' : 'tomas'}`,
  }
}

function PillCounter({ card, days }: PillCounterProps) {
  const t = totalEntrega(card, days)
  if (!t) return null
  return (
    <div className="grid gap-0.5 mt-1" aria-label="Entregar al cliente">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
          Entregar al cliente
        </span>
        <span className="text-[15px] font-semibold tabular-nums" style={{ color: 'var(--color-brand-ink)' }}>
          {t.texto}
        </span>
      </div>
      <div className="text-[12px] tabular-nums text-right" style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>
        {t.detalle}
      </div>
    </div>
  )
}

function CompactTotal({ card, days }: PillCounterProps) {
  const t = totalEntrega(card, days)
  if (!t) return null
  return (
    <div className="text-[13px] tabular-nums" style={{ color: 'var(--color-brand-ink)', fontFamily: 'var(--font-mono)' }}>
      Total: {t.texto}
    </div>
  )
}
